<template>
    <div>
        <div class="d-flex align-center mb-1">
            <v-tooltip bottom small>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn icon class="mr-2" :to="{name: 'orders'}" exact v-bind="attrs" v-on="on">
                        <v-icon>mdi-arrow-left</v-icon>
                    </v-btn>
                </template>
                <span>{{ $t('views.main.orders.web_app_order.back_to_list') }}</span>
            </v-tooltip>

            <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn
                        icon
                        exact
                        v-bind="attrs"
                        v-on="on"
                        @click="showConvertOrderIntoDraftDialog"
                    >
                        <v-icon>mdi-pencil-outline</v-icon>
                    </v-btn>
                </template>
                <span>{{ $t('views.main.orders.web_app_order.order_edit_button') }}</span>
            </v-tooltip>

            <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on" @click="showOrderDeleteDialog">
                        <v-icon>mdi-delete-outline</v-icon>
                    </v-btn>
                </template>
                <span>{{ $t('views.main.orders.web_app_order.order_delete_button') }}</span>
            </v-tooltip>
        </div>

        <v-divider class="my-2"></v-divider>

        <v-row class="align-center my-1">
            <v-col cols="6">
                <div class="text-subtitle-1 font-weight-bold">
                    <span class="d-none d-sm-inline">{{ $t('views.main.orders.web_app_order.order') }}</span>
                    {{ order.OrderName }}
                </div>
            </v-col>
            <v-col cols="6" class="text-right">
                <order-status-chip
                    :order-status="order.OrderStatus"
                    :order-status-name="order.OrderStatusName"
                ></order-status-chip>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12" md="10" order="2" order-md="1">
                <block-card
                    v-for="(OrderedBlock, index) in OrderedBlocks"
                    :key="'block-card-'+index"
                    :units="OrderedBlock.Units"
                    :block="OrderedBlock.Block"
                    :block-price="OrderedBlock.BlockPrice"
                    :index="index"
                    :static="true"
                ></block-card>
            </v-col>
            <v-col cols="12" md="2" order="1" order-md="2">
                <div class="text--secondary text-body-2">
                    <v-icon>
                        mdi-calendar-clock
                    </v-icon>
                    {{ $t('views.main.orders.web_app_order.order_from_day') }}
                </div>
                <div class="text-body-1 mb-5">
                    {{ order.OrderDate | dateFormat }} <small>{{ order.OrderDate | timeFormat }}</small>
                </div>

                <template v-if="order.ShipmentType">
                    <div class="text--secondary text-body-2">
                        <v-icon>
                            mdi-truck-outline
                        </v-icon>
                        {{ $t('views.main.orders.web_app_order.transport') }}
                    </div>
                    <div class="text-body-1 mb-5">
                        {{ order.ShipmentType.TypeName }}
                    </div>
                </template>

                <template v-if="order.ClientOrderName">
                    <div class="text--secondary text-body-2">
                        <v-icon>
                            mdi-counter
                        </v-icon>
                        {{ $t('views.main.orders.web_app_order.client_order_name') }}
                    </div>
                    <div class="text-body-1 mb-5">
                        {{ order.ClientOrderName }}
                    </div>
                </template>

                <template v-if="order.WebAppComments">
                    <div class="text--secondary text-body-2">
                        <v-icon>
                            mdi-bullhorn-outline
                        </v-icon>
                        {{ $t('views.main.orders.web_app_order.web_app_comments') }}
                    </div>
                    <div class="text-body-1 mb-5">
                        {{ order.WebAppComments }}
                    </div>
                </template>

                <template v-if="order.CustomerName">
                    <div class="text--secondary text-body-2">
                        <v-icon>
                            mdi-account-outline
                        </v-icon>
                        {{ $t('views.main.orders.web_app_order.customer_name') }}
                    </div>
                    <div class="text-body-1 mb-5">
                        {{ order.CustomerName }}
                    </div>
                </template>
            </v-col>
        </v-row>

        <v-dialog
            v-model="OrderDeleteDialog"
            persistent
            width="500"
        >
            <v-card>
                <v-card-title class="headline grey lighten-2 mb-5">
                    {{ $t('views.main.orders.web_app_order.order_removal') }}
                    <v-spacer></v-spacer>
                    <v-btn
                        icon
                        @click="hideOrderDeleteDialog"
                    >
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>

                <v-card-text v-if="order.IsEditable">
                    {{ $t('views.main.orders.web_app_order.sure_to_remove_order_with_number') }}
                    <strong>{{ order.OrderName }}</strong>?
                </v-card-text>
                <v-card-text v-else>
                    {{ $t('views.main.orders.web_app_order.cant_remove_orders_with_status') }}
                    <strong>{{ order.OrderStatusName }}</strong>.
                </v-card-text>

                <v-divider></v-divider>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                        v-if="order.IsEditable"
                        color="error"
                        rounded
                        :loading="OrderDeleting"
                        @click="orderDelete"
                    >
                        {{ $t('views.main.orders.web_app_order.delete_button') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog
            v-model="ConvertOrderIntoDraftDialog"
            persistent
            width="500"
        >
            <v-card>
                <v-card-title class="headline grey lighten-2 mb-5">
                    {{ $t('views.main.orders.web_app_order.warning') }}
                    <v-spacer></v-spacer>
                    <v-btn
                        icon
                        @click="hideConvertOrderIntoDraftDialog"
                    >
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>

                <v-card-text v-if="order.IsEditable">
                    <p>
                        {{ $t('views.main.orders.web_app_order.convert_to_draft_info') }}
                    </p>
                    <p>
                        {{ $t('views.main.orders.web_app_order.remember_to_finish_process') }}
                    </p>
                    <p>{{ $t('views.main.orders.web_app_order.sure_to_continue') }}</p>
                </v-card-text>
                <v-card-text v-else>
                    {{ $t('views.main.orders.web_app_order.cant_edit_orders_with_status') }}
                    <strong>{{ order.OrderStatusName }}</strong>.
                </v-card-text>

                <v-divider></v-divider>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                        v-if="order.IsEditable"
                        color="warning"
                        rounded
                        :loading="ConvertingOrderIntoDraft"
                        @click="draftStore"
                    >
                        {{ $t('views.main.orders.web_app_order.continue_button') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import BlockCard from "../../../components/BlockCard";
import OrderStatusChip from "../../../components/OrderStatusChip";

export default {
    name: "WebAppOrder",

    components: {
        BlockCard,
        OrderStatusChip,
    },

    props: {
        order: Object,
    },

    data() {
        return {
            OrderDeleteDialog: false,
            OrderDeleting: false,

            ConvertOrderIntoDraftDialog: false,
            ConvertingOrderIntoDraft: false,
        }
    },

    computed: {
        OrderExists() {
            return this.order !== null;
        },
        OrderedBlocks() {
            return this.OrderExists
                ? this.order.OrderedBlocks
                : [];
        },
    },

    methods: {
        orderDelete: function () {
            this.OrderDeleting = true;

            this.$store.dispatch('orderDelete', this.order.OrderID).then(response => {
                if(response.status === 204) {
                    this.hideOrderDeleteDialog();
                    this.$router.push({name: "orders"});
                } else {
                    this.$store.commit(
                        "showSnackbarError",
                        this.$t('views.main.orders.web_app_order.order_deleting_failed')
                    );
                }
            })
                .catch(() => {})
                .then(() => {
                    this.OrderDeleting = false;
                });
        },
        showOrderDeleteDialog: function () {
            this.OrderDeleteDialog = true;
        },
        hideOrderDeleteDialog: function () {
            this.OrderDeleteDialog = false;
        },
        draftStore: function () {
            this.ConvertingOrderIntoDraft = true;

            this.$store.dispatch('draftStore', {
                OrderID: this.order.OrderID
            }).then(response => {
                if([200, 201].includes(response.status)) {
                    this.hideConvertOrderIntoDraftDialog();
                    this.$router.push({name: "draft_edit", params: { id: response.data.data.DraftID }});
                } else {
                    this.$store.commit(
                        "showSnackbarError",
                        this.$t('views.main.orders.web_app_order.conversion_to_draft_failed')
                    );
                }
            })
                .catch(() => {})
                .then(() => {
                    this.ConvertingOrderIntoDraft = false;
                });
        },
        showConvertOrderIntoDraftDialog: function () {
            this.ConvertOrderIntoDraftDialog = true;
        },
        hideConvertOrderIntoDraftDialog: function () {
            this.ConvertOrderIntoDraftDialog = false;
        },
    },
}
</script>

<style scoped>

</style>
